import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { ConfirmModal, toast } from '@feathr/components';
import CampaignSummary from '@feathr/extender/App/EventsPage/CampaignSummary';

interface ICampaignEnableModalProps {
  campaign: Campaign;
  closeEnableModal: () => void;
  isEnableModalOpen: boolean;
}

/*
 * This modal needs to be reimplemented when we adjust the logic on the buttons that trigger it
 * to include validation and utilize right republish logic below. It should work similarly to
 * SaveCampaignButton.
 */
function CampaignEnableModal({
  campaign,
  isEnableModalOpen,
  closeEnableModal,
}: Readonly<ICampaignEnableModalProps>): JSX.Element {
  const { t } = useTranslation();

  async function handleEnable(): Promise<void> {
    try {
      const newState = CampaignState.Published;
      campaign.set({
        is_enabled: true,
        is_archived: false,
        state: newState,
      });
      await campaign.save();
      toast(t('Campaign republished! It will start running shortly.'));

      closeEnableModal();
    } catch (error: any) {
      toast(
        t('Encountered an error while enabling:\n{{- errorMessage}}', {
          errorMessage: error.message,
        }),
        {
          type: 'error',
        },
      );
    }
  }

  return (
    <ConfirmModal
      confirmationPhrase={t('ENABLE')}
      confirmButtonText={t('Republish campaign')}
      confirmButtonType={'success'}
      description={t('This will resume ad delivery. Your campaign will start running again.')}
      onClose={closeEnableModal}
      onConfirm={handleEnable}
      opened={isEnableModalOpen}
      t={t}
      title={t('Republish this campaign?')}
    >
      <CampaignSummary campaign={campaign} layout={'vertical'} showMinimal={true} />
    </ConfirmModal>
  );
}

export default CampaignEnableModal;
